import { createContext, useContext, useEffect, useState } from "react";
import { t as translate } from "@/i18n/translations";

const LanguageContext = createContext(null);

const STORAGE_KEY = "lang";
const STORAGE_EXPLICIT_KEY = "lang_explicit"; // "1" once the user has manually toggled

// Decide the *initial* language for a fresh visitor:
//   1. If the user has explicitly chosen one (toggle clicked), respect it.
//   2. Else auto-detect from `navigator.language` / `navigator.languages` —
//      Spanish-speaking browsers (es-*) → "es"; everything else → "en".
//
// We intentionally IGNORE any pre-existing localStorage `lang` value when
// there is no `lang_explicit` flag. Reason: prior to this revision the app
// defaulted to "es" for everyone, so a stored "es" doesn't actually reflect a
// user choice — only the old hardcoded default. Re-detect on next visit so
// English-only browsers see English.
function detectInitialLang() {
  try {
    if (typeof window === "undefined") return "en";
    const explicit = localStorage.getItem(STORAGE_EXPLICIT_KEY);
    const stored = localStorage.getItem(STORAGE_KEY);
    if (explicit === "1" && (stored === "es" || stored === "en")) return stored;
    const candidates = [
      ...(navigator.languages || []),
      navigator.language,
      navigator.userLanguage,
    ].filter(Boolean);
    for (const raw of candidates) {
      const lang = String(raw).toLowerCase();
      if (lang.startsWith("es")) return "es";
    }
    return "en";
  } catch {
    return "en";
  }
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
      if (typeof document !== "undefined") {
        document.documentElement.lang = lang;
      }
    } catch (e) { void e; }
  }, [lang]);

  // Wrap setters so that ANY manual change (toggle or direct set) flags the
  // user's preference as explicit → from now on we never auto-detect again on
  // this device.
  const setLang = (next) => {
    try { localStorage.setItem(STORAGE_EXPLICIT_KEY, "1"); } catch (e) { void e; }
    setLangState(next);
  };
  const toggle = () => setLang(lang === "es" ? "en" : "es");

  const t = (key) => translate(key, lang);

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
